import type { RequestOptions, SearchRequest } from 'searchkit';

import type { SearchkitWrapperProps } from '@uncinc/react-searchkit/dist/components/Wrapper';

import { runtimeMappings } from '../SearchTopicalEvents/config';

const resultAttributes = [
  'type',
  'url',
  'nid',
  'created',
  'page_title',
  'body_summarized',
  'image_style_uri',
  'read_time',
  'event_date',
  'event_date_end_value',
  'event_date_timezone',
  'event_date_infinite',
  'computed_first_upcoming_date_index',
];

export const hitsPerPage = 4;

export const searchSettings: SearchkitWrapperProps['searchSettings'] = {
  search_attributes: [],
  result_attributes: resultAttributes,
  runtime_mappings: runtimeMappings,
  sorting: {
    default: {
      field: 'created',
      order: 'desc',
    },
  },
};

// Mixed articles + events filtered by theme. Upcoming-event filter is embedded
// in getBaseFilters so past events are excluded without a beforeSearch hook.
export const byThemeRequestOptions = (themeIds: number[], excludeNid?: number): RequestOptions => ({
  getBaseFilters: () => [
    { terms: { themes_tid: themeIds } },
    {
      bool: {
        should: [
          { term: { type: 'article' } },
          {
            bool: {
              must: [
                { term: { type: 'event' } },
                { exists: { field: 'computed_first_upcoming_date_index' } },
              ],
            },
          },
        ],
        minimum_should_match: 1,
      },
    },
    ...(excludeNid !== undefined ? [{
      bool: {
        must_not: [
          { term: { nid: excludeNid } },
        ],
      },
    }] : []),
  ],
  hooks: {
    beforeSearch: (requests: SearchRequest[]) => {
      requests.forEach((request) => {
        request.body.query = {
          bool: {
            should: [
              {
                // Boost events over articles.
                function_score: {
                  query: request.body.query,
                  functions: [
                    {
                      filter: {
                        bool: {
                          must: [
                            { term: { type: 'event' } },
                            { exists: { field: 'computed_first_upcoming_date_index' } },
                          ],
                        },
                      },
                      weight: 4,
                    },
                  ],
                  boost_mode: 'replace',
                },
              },
            ],
          },
        };
      });

      return Promise.resolve(requests);
    },
  },
});

export const byTargetAudienceArticlesRequestOptions =
  (targetAudienceId: number): RequestOptions => ({
    getBaseFilters: () => [
      { term: { type: 'article' } },
      { terms: { target_audiences_tid: [targetAudienceId] } },
    ],
  });

export const byTargetAudienceEventsRequestOptions =
  (targetAudienceId: number): RequestOptions => ({
    getBaseFilters: () => [
      { term: { type: 'event' } },
      { terms: { target_audiences_tid: [targetAudienceId] } },
      { exists: { field: 'computed_first_upcoming_date_index' } },
    ],
  });

export const byProjectRequestOptions =
  (projectId: number): RequestOptions => ({
    getBaseFilters: () => [
      { term: { type: 'article' } },
      { terms: { projects_nid: [projectId] } },
    ],
  });

